/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import { Button } from '../ui/button';

interface DashboardPageProps {
  setActiveTab: (tab: string) => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ setActiveTab }) => {
  const [statistics, setStatistics] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalLessons: 0,
    totalPresaleUsers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [usersResponse, coursesResponse, presaleResponse] = await Promise.all([
        services.get(API_ENDPOINTS.USERS.LIST),
        services.get(API_ENDPOINTS.COURSES.LIST),
        services.get(API_ENDPOINTS.PRESALE.LIST),
      ]);

      // Count non-admin users
      const allUsers = usersResponse.data.data || usersResponse.data || [];
      const nonAdminUsers = allUsers.filter((user: any) => user.role !== 'admin');

      // Count courses
      const courses = coursesResponse.data.data || coursesResponse.data || [];

      // Count presale users
      const presaleUsers = presaleResponse.data.data || presaleResponse.data || [];

      // OPTIMIZED: Get total lessons count with a single API call
      let totalLessons = 0;
      try {
        const lessonsResponse = await services.get(
          `${API_ENDPOINTS.LESSONS.LIST}?page=1&limit=1`
        );
        totalLessons = lessonsResponse.data.total || 0;
        console.log('📊 Dashboard - Total lessons count:', totalLessons);
      } catch (err) {
        console.log('Could not fetch total lessons count:', err);
      }

      setStatistics({
        totalUsers: nonAdminUsers.length,
        totalCourses: courses.length,
        totalLessons: totalLessons,
        totalPresaleUsers: presaleUsers.length,
      });
    } catch (err: any) {
      console.error('Failed to fetch statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='px-4 py-6'>
      {/* Welcome Section */}
      <div className='mb-10'>
        <h1
          className='text-4xl font-bold mb-4 font-dm-serif'
          style={{
            color: '#ede8c5',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            letterSpacing: '0.5px',
          }}
        >
          Welcome to VESTN Admin Panel
        </h1>
        <p
          className='text-lg font-work-sans'
          style={{
            color: '#ede8c5',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
          }}
        >
          Manage your platform efficiently with our comprehensive admin tools.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10'>
        <div
          className='rounded-lg p-8 border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Total Users
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {loading ? '...' : statistics.totalUsers}
              </p>
            </div>
          </div>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15v-4h4v4H8zm6-6H6V8h8v1z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Total Courses
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {loading ? '...' : statistics.totalCourses}
              </p>
            </div>
          </div>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Total Lessons
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {loading ? '...' : statistics.totalLessons}
              </p>
            </div>
          </div>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
          }}
        >
          <div className='flex items-center'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div>
              <p className='text-sm font-medium font-work-sans' style={{ color: '#ede8c5' }}>
                Presale Users
              </p>
              <p className='text-2xl font-bold font-dm-serif' style={{ color: '#ede8c5' }}>
                {loading ? '...' : statistics.totalPresaleUsers}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10'>
        <div
          className='rounded-lg p-8 border shadow-lg flex flex-col h-full'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
            minHeight: '280px',
          }}
        >
          <div className='flex items-center mb-4'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
            </div>
            <h3 className='text-xl font-semibold font-dm-serif' style={{ color: '#ede8c5' }}>
              User Management
            </h3>
          </div>
          <p className='mb-6 font-work-sans flex-grow' style={{ color: '#ede8c5' }}>
            Manage user accounts, roles, and permissions across your platform.
          </p>
          <Button
            onClick={() => setActiveTab('users')}
            className='w-full font-work-sans font-semibold mt-auto'
            style={{
              backgroundColor: '#bbe425',
              color: '#0B222C',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(187, 228, 37, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            Manage Users
          </Button>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg flex flex-col h-full'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
            minHeight: '280px',
          }}
        >
          <div className='flex items-center mb-4'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <h3 className='text-xl font-semibold font-dm-serif' style={{ color: '#ede8c5' }}>
              Admin Management
            </h3>
          </div>
          <p className='mb-6 font-work-sans flex-grow' style={{ color: '#ede8c5' }}>
            Create and manage admin accounts with full platform access and permissions.
          </p>
          <Button
            onClick={() => setActiveTab('admin')}
            className='w-full font-work-sans font-semibold mt-auto'
            style={{
              backgroundColor: '#bbe425',
              color: '#0B222C',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(187, 228, 37, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            Manage Admins
          </Button>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg flex flex-col h-full'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
            minHeight: '280px',
          }}
        >
          <div className='flex items-center mb-4'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15v-4h4v4H8zm6-6H6V8h8v1z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <h3 className='text-xl font-semibold font-dm-serif' style={{ color: '#ede8c5' }}>
              Course Management
            </h3>
          </div>
          <p className='mb-6 font-work-sans flex-grow' style={{ color: '#ede8c5' }}>
            Create, edit, and manage courses and lessons for your educational platform.
          </p>
          <Button
            onClick={() => setActiveTab('courses')}
            className='w-full font-work-sans font-semibold mt-auto'
            style={{
              backgroundColor: '#bbe425',
              color: '#0B222C',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(187, 228, 37, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            Manage Courses
          </Button>
        </div>

        <div
          className='rounded-lg p-8 border shadow-lg flex flex-col h-full'
          style={{
            backgroundColor: '#0B222C',
            borderColor: '#095D52',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
            minHeight: '280px',
          }}
        >
          <div className='flex items-center mb-4'>
            <div className='p-3 rounded-lg mr-4'>
              <svg
                className='w-6 h-6'
                style={{ color: '#095D52' }}
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <h3 className='text-xl font-semibold font-dm-serif' style={{ color: '#ede8c5' }}>
              Presale Management
            </h3>
          </div>
          <p className='mb-6 font-work-sans flex-grow' style={{ color: '#ede8c5' }}>
            View and manage presale user registrations and their status.
          </p>
          <Button
            onClick={() => setActiveTab('presale')}
            className='w-full font-work-sans font-semibold mt-auto'
            style={{
              backgroundColor: '#bbe425',
              color: '#0B222C',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(187, 228, 37, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            Manage Presale
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
